import { thesisApiBase } from '@/lib/thesisPlatform'
import { useSubscriptionStore } from '@/stores/subscriptionStore'

export type ThesisCloudProjectMeta = {
  id: string
  localNovelId: string
  title: string
  clientVersion: number
  updatedAt: string
}

export type ThesisCloudPayload = {
  schemaVersion: 1
  brief: unknown
  entities: unknown[]
}

async function api<T>(
  path: string,
  init: RequestInit = {},
): Promise<{ ok: true; data: T } | { ok: false; status: number; error: string; code?: string; serverVersion?: number }> {
  const base = thesisApiBase()
  if (!base) return { ok: false, status: 0, error: '未配置 API', code: 'NO_API' }
  const token = useSubscriptionStore.getState().token
  if (!token) return { ok: false, status: 401, error: '请先登录', code: 'UNAUTHORIZED' }
  const headers = new Headers(init.headers)
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  headers.set('Authorization', `Bearer ${token}`)
  try {
    const res = await fetch(`${base}${path}`, { ...init, headers, cache: 'no-store' })
    const text = await res.text()
    let json: Record<string, unknown> = {}
    try {
      json = text ? JSON.parse(text) : {}
    } catch {
      json = { error: text }
    }
    if (!res.ok) {
      return {
        ok: false,
        status: res.status,
        error: String(json.error || res.statusText),
        code: json.code ? String(json.code) : undefined,
        serverVersion: typeof json.serverVersion === 'number' ? json.serverVersion : undefined,
      }
    }
    return { ok: true, data: json as T }
  } catch (e) {
    return { ok: false, status: 0, error: e instanceof Error ? e.message : '网络错误', code: 'NETWORK' }
  }
}

export function listCloudProjects() {
  return api<{ projects: ThesisCloudProjectMeta[] }>('/api/projects')
}

export function getCloudProjectMeta(id: string) {
  return api<ThesisCloudProjectMeta>(`/api/projects/${id}/meta`)
}

export function getCloudProject(id: string) {
  return api<ThesisCloudProjectMeta & { payload: ThesisCloudPayload }>(`/api/projects/${id}`)
}

export function getCloudProjectByLocal(localNovelId: string) {
  return api<ThesisCloudProjectMeta & { payload: ThesisCloudPayload }>(
    `/api/projects/by-local/${encodeURIComponent(localNovelId)}`,
  )
}

export function createCloudProject(args: {
  localNovelId: string
  title: string
  payload: ThesisCloudPayload
}) {
  return api<ThesisCloudProjectMeta & { created?: boolean }>('/api/projects', {
    method: 'POST',
    body: JSON.stringify(args),
  })
}

export function putCloudProject(
  id: string,
  args: {
    clientVersion: number
    title: string
    payload: ThesisCloudPayload
    forceOverwrite?: boolean
  },
) {
  return api<ThesisCloudProjectMeta>(`/api/projects/${id}`, {
    method: 'PUT',
    body: JSON.stringify(args),
  })
}

export function putCloudByLocal(
  localNovelId: string,
  args: {
    clientVersion: number
    title: string
    payload: ThesisCloudPayload
    forceOverwrite?: boolean
  },
) {
  return api<ThesisCloudProjectMeta & { created?: boolean }>(
    `/api/projects/by-local/${encodeURIComponent(localNovelId)}`,
    {
      method: 'PUT',
      body: JSON.stringify(args),
    },
  )
}
